import { useState, FormEvent } from 'react';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d6slg0id1d31f3q961t0';

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value.toString()));

    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
        setCharCount(0);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: 'ri-phone-line', label: 'Phone', value: '(267) 773-1129', href: 'tel:2677731129' },
    { icon: 'ri-map-pin-line', label: 'Service Area', value: 'Philadelphia, PA', href: null },
    { icon: 'ri-time-line', label: 'Availability', value: 'Prom Season 2026', href: null },
  ];

  return (
    <section id="contact" className="py-28 bg-[#080808] relative">
      <div className="max-w-6xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center mb-14">
          <p className="text-gray-500 text-xs tracking-[0.35em] uppercase mb-4">Get In Touch</p>
          <h2 className="font-display silver-gradient-text text-5xl md:text-6xl font-semibold tracking-wide mb-6">
            Contact Us
          </h2>
          <p className="text-gray-400 text-sm tracking-wide leading-relaxed max-w-xl mx-auto">
            Ready to make your Philadelphia prom night unforgettable? Reach out — we'd love to help you plan the perfect luxury chauffeur experience.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-10">
          {/* Left: Info */}
          <div>
            <div className="mb-8">
              <h3 className="font-display silver-gradient-text text-3xl font-semibold mb-4">
                Luxury Prom Chauffeur Experience
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                Philadelphia's most exclusive prom night transportation service. Arrive in a stunning 2026 BMW with a professional chauffeur. Book early — prom season dates are limited.
              </p>
              <div className="silver-border-bright rounded-2xl p-1 inline-block mb-6">
                <div className="bg-[#111] rounded-xl px-5 py-3">
                  <p className="text-gray-500 text-xs tracking-wider uppercase mb-1">Call or Text Now</p>
                  <a href="tel:2677731129" className="font-display silver-gradient-text text-3xl font-semibold tracking-wide cursor-pointer hover:opacity-80 transition-opacity">
                    (267) 773-1129
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              {contactInfo.map((item) => (
                <div key={item.label} className="flex items-center gap-4 bg-[#111] border border-gray-800 rounded-xl p-4">
                  <div className="w-10 h-10 flex items-center justify-center silver-border rounded-full shrink-0">
                    <i className={`${item.icon} text-gray-400 text-base`}></i>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs tracking-[0.1em] uppercase mb-0.5">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-white text-sm font-medium hover:text-gray-300 cursor-pointer">{item.value}</a>
                    ) : (
                      <p className="text-white text-sm font-medium">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Hero Image */}
            <div className="mt-8 rounded-2xl overflow-hidden h-48">
              <img
                src="https://public.readdy.ai/ai/img_res/8a1e9226deed8beb24ab171004f117ef.jpg"
                alt="Philadelphia luxury prom chauffeur service"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right: Form */}
          <div>
            {submitted ? (
              <div className="card-luxury rounded-2xl p-10 text-center h-full flex flex-col items-center justify-center">
                <div className="w-14 h-14 flex items-center justify-center silver-border rounded-full mb-5">
                  <i className="ri-check-line text-xl text-gray-300"></i>
                </div>
                <h3 className="font-display silver-gradient-text text-2xl font-semibold mb-3">Message Sent!</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Thanks for reaching out! We'll get back to you shortly. Feel free to call us at <a href="tel:2677731129" className="text-gray-300 hover:text-white cursor-pointer">(267) 773-1129</a>.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 btn-outline-silver rounded-full px-8 py-3 text-xs tracking-[0.15em] uppercase cursor-pointer whitespace-nowrap"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="card-luxury rounded-2xl p-8 space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="Your name"
                      className="luxury-input w-full rounded-lg px-4 py-3"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="(215) 000-0000"
                      className="luxury-input w-full rounded-lg px-4 py-3"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="luxury-input w-full rounded-lg px-4 py-3"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Subject</label>
                  <select name="subject" className="luxury-input w-full rounded-lg px-4 py-3 cursor-pointer">
                    <option value="">Select a topic...</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Booking Question">Booking Question</option>
                    <option value="Pricing Information">Pricing Information</option>
                    <option value="Availability Check">Availability Check</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">
                    Message * <span className="text-gray-600 normal-case tracking-normal text-xs">{charCount}/500</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Your message... (max 500 characters)"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="luxury-input w-full rounded-lg px-4 py-3 resize-none"
                  ></textarea>
                  {charCount >= 500 && (
                    <p className="text-red-400 text-xs mt-1">Character limit reached. Please shorten your message.</p>
                  )}
                </div>

                {error && <p className="text-red-400 text-xs text-center">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting || charCount >= 500}
                  className="w-full btn-silver rounded-xl py-4 text-xs tracking-[0.2em] uppercase cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
